import * as React from "react";
import { motion } from "framer-motion";
import { Backspace } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { CashierLabels } from "./types";

export type KeypadMode = "qty" | "amount" | "percent";

export interface CashierNumericKeypadProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (v: string) => void;
  mode?: KeypadMode;
  max?: number;
  labels: CashierLabels;
  className?: string;
  submitLabel?: string;
}

export function CashierNumericKeypad({
  value, onChange, onSubmit, mode = "amount", max, labels, className, submitLabel,
}: CashierNumericKeypadProps) {
  const press = (key: string) => {
    let next = value;
    if (key === "⌫") {
      next = next.slice(0, -1);
    } else if (key === "C") {
      next = "";
    } else if (key === "00") {
      next = next + "00";
    } else {
      next = next + key;
    }
    next = next.replace(/^0+(\d)/, "$1");
    if (mode === "qty") next = next.replace(/\D/g, "");
    if (max != null && Number(next || "0") > max) next = String(max);
    if (mode === "percent" && Number(next || "0") > 100) next = "100";
    onChange(next);
  };

  const KEYS: string[] = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "00", "0", "⌫"];

  return (
    <div className={cn("flex gap-2", className)}>
      <div className="grid grid-cols-3 gap-2 flex-1">
        {KEYS.map((k) => (
          <KeyButton key={k} onClick={() => press(k)} ariaLabel={k === "⌫" ? labels.keypad.backspace : k}>
            {k === "⌫" ? <Backspace className="h-6 w-6" /> : k}
          </KeyButton>
        ))}
      </div>
      <div className="flex flex-col gap-2 w-24">
        <KeyButton onClick={() => onChange("")} ariaLabel={labels.keypad.clear} variant="ghost">
          {labels.keypad.clear}
        </KeyButton>
        <button
          type="button"
          onClick={() => onSubmit(value)}
          className={cn(
            "flex-1 rounded-biz-btn bg-biz-primary text-biz-primary-foreground font-semibold",
            "biz-shadow-md hover:bg-biz-primary-hover transition-colors biz-focus-ring",
          )}
        >
          {submitLabel ?? labels.keypad.enter}
        </button>
      </div>
    </div>
  );
}

function KeyButton({
  children, onClick, ariaLabel, variant = "default",
}: {
  children: React.ReactNode; onClick: () => void; ariaLabel: string;
  variant?: "default" | "ghost";
}) {
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.94 }}
      transition={{ duration: 0.1 }}
      aria-label={ariaLabel}
      onClick={onClick}
      className={cn(
        "min-h-[64px] rounded-biz-btn font-mono-num text-2xl font-semibold tabular-nums",
        "biz-focus-ring transition-colors",
        variant === "default"
          ? "bg-biz-surface-elevated text-biz-foreground border border-biz-border hover:bg-biz-muted"
          : "bg-biz-muted text-biz-foreground-muted hover:bg-biz-border",
      )}
    >
      {children}
    </motion.button>
  );
}
